if (!customElements.get('apex-eligible-pagination')) {
  customElements.define('apex-eligible-pagination', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.start());
    }
    disconnectedCallback() {
      this.controller?.abort();
    }
    start() {
      if (!this.isConnected || this.started) return;
      this.started = true;
      this.list = this.querySelector('product-list');
      this.button = this.querySelector('[data-load]');
      this.status = this.querySelector('[role="status"]');
      this.fallback = this.querySelector('[data-fallback]');
      if (!this.list || !this.button) return;
      this.next = this.dataset.next;
      this.queue = [];
      this.seen = new Set(Array.from(this.list.querySelectorAll('product-card'), card => card.getAttribute('handle')));
      this.visited = new Set();
      this.size = Number(this.dataset.size) || 24;
      this.controller = new AbortController();
      this.button.addEventListener('click', () => this.load(this.size, true), {signal: this.controller.signal});
      this.context = location.pathname + location.search + '|' + this.dataset.section + '|' + this.size;
      this.addEventListener('click', event => {
        const link = event.target.closest('product-card a[href]');
        if (link && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
          this.save(link.closest('product-card'));
        }
      }, {capture: true, signal: this.controller.signal});
      const saved = history.state?.apexEligible;
      this.fallback.hidden = true;
      this.update();
      if (saved?.context === this.context && Number.isInteger(saved.count) && saved.count > 0 && saved.count <= 10000) {
        this.restore(saved);
        return;
      }
      const missing = this.size - this.seen.size;
      if (missing > 0 && this.next) this.load(missing, false);
    }
    save(card) {
      if (this.restoring || this.context !== location.pathname + location.search + '|' + this.dataset.section + '|' + this.size) return;
      const previous = history.state?.apexEligible;
      const anchor = card ? {handle: card.getAttribute('handle'), offset: card.getBoundingClientRect().top} : previous?.context === this.context ? previous.anchor : null;
      history.replaceState({...history.state, apexEligible: {context: this.context, count: this.seen.size, anchor}}, '');
    }
    async restore(saved) {
      this.restoring = true;
      while (this.isConnected && this.seen.size < saved.count && (this.next || this.queue.length)) {
        const before = this.visited.size;
        const count = this.seen.size;
        await this.load(saved.count - count, false);
        if (before === this.visited.size && count === this.seen.size) break;
      }
      this.restoring = false;
      if (!this.isConnected) return;
      if (saved.anchor) {
        const card = Array.from(this.list.querySelectorAll('product-card')).find(el => el.getAttribute('handle') === saved.anchor.handle);
        if (card && Number.isFinite(saved.anchor.offset)) {
          requestAnimationFrame(() => {
            if (!this.isConnected) return;
            card.querySelector('a')?.focus({preventScroll: true});
            window.scrollBy(0, card.getBoundingClientRect().top - saved.anchor.offset);
          });
        }
      }
      this.save();
    }
    update() {
      this.button.hidden = !this.next && !this.queue.length;
      if (!this.seen.size && !this.next && !this.queue.length) this.status.textContent = this.dataset.empty;
    }
    async load(target, focusNew) {
      if (this.busy) return;
      this.busy = true;
      this.button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      let added = 0, requests = 0;
      try {
        while (added < target && this.isConnected) {
          if (!this.queue.length) {
            if (!this.next || requests >= 5) break;
            const url = new URL(this.next, location.href);
            if (url.origin !== location.origin) throw new Error('Invalid collection origin');
            url.searchParams.set('section_id', this.dataset.section);
            if (this.visited.has(url.href)) throw new Error('Repeated collection page');
            const response = await fetch(url, {signal: this.controller.signal});
            if (!response.ok) throw new Error('Collection request failed');
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const page = Array.from(doc.querySelectorAll('apex-eligible-pagination')).find(el => el.dataset.section === this.dataset.section);
            const list = page?.querySelector('product-list');
            if (!list) throw new Error('Missing collection results');
            this.visited.add(url.href);
            this.next = page.dataset.next;
            this.queue.push(...Array.from(list.children));
            requests++;
          }
          while (this.queue.length && added < target) {
            const node = this.queue.shift();
            if (node.matches('product-card')) {
              const handle = node.getAttribute('handle');
              if (this.seen.has(handle)) continue;
              this.seen.add(handle);
              node.removeAttribute('reveal-on-scroll');
              added++;
            }
            this.list.append(document.importNode(node, true));
          }
        }
        if (!this.isConnected) return;
        this.fallback.hidden = true;
        this.status.textContent = this.dataset.added.replace('[count]', added);
        this.update();
        this.save();
        if (focusNew && added) {
          const cards = this.list.querySelectorAll('product-card');
          cards[cards.length - added]?.querySelector('a')?.focus({preventScroll: true});
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.status.textContent = this.dataset.error;
          this.fallback.hidden = false;
        }
      } finally {
        this.busy = false;
        this.button.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
  });
}
